import { usePage } from "@inertiajs/react"
import { useCallback } from "react"

import type { TranslationNode, TranslationValue } from "@/types"

export type Translate = (key: string, vars?: Record<string, string | number>) => string

function interpolate(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text
  return text.replace(/%\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match
  )
}

function pluralized(node: TranslationNode, locale: string, count: number): string | undefined {
  const rule = new Intl.PluralRules(locale.replace("_", "-")).select(count)
  const value = node[rule] ?? node.other ?? node.one
  return typeof value === "string" ? value : undefined
}

function resolve(
  key: string,
  translations: TranslationNode,
  locale: string,
  vars?: Record<string, string | number>
): string {
  let value: TranslationValue | undefined = translations

  for (const part of key.split(".")) {
    if (value === undefined || typeof value === "string") return key
    value = value[part]
  }

  if (value === undefined) return key

  if (typeof value === "object" && typeof vars?.count === "number") {
    value = pluralized(value, locale, vars.count) ?? value
  }

  return typeof value === "string" ? interpolate(value, vars) : key
}

export function useTranslations() {
  const { i18n } = usePage().props

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>): string =>
      i18n ? resolve(key, i18n.translations, i18n.locale, vars) : key,
    [i18n]
  )

  return { t, locale: i18n?.locale ?? "pt-BR" }
}
